import {
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  ISignUpResult,
} from 'amazon-cognito-identity-js';
import { randomUUID as uuidv4 } from 'expo-crypto';

import { getConfigValue } from 'utils/environment';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { withNetworkRetries } from 'utils/network/utils';

import { secureAuthStorage } from './secure-auth-storage';
import normalizeString, { getCognitoRetryConfig } from './util';

function attributeObjectToAttributeList(attributes: ISignUpAttributes | IUpdateUserAttributes) {
  return Object.entries(attributes).flatMap(([Name, Value]) =>
    Value == null ? [] : [new CognitoUserAttribute({ Name, Value })]
  );
}

interface ISignUp {
  username: string;
  name: string;
  phoneNumber: string;
  wantsPromotionalEmails: boolean;
  country: string;
  dob?: string;
}

interface IValidateLogin {
  username: string;
  code: string;
  sessionId: string;
}

interface ISignUpAttributes {
  nickname: string;
  name: string;
  locale: string;
  phone_number?: string;
  birthdate?: string;
}

interface IUpdateUserAttributes {
  name?: string;
  phoneNumber?: string | null;
  dob?: string;
}

export const commitAuthenticatedSession = (session: CognitoUserSession): CognitoUserSession => {
  const username = session.getAccessToken().payload.username;
  if (typeof username !== 'string' || !username) {
    throw new Error('Cognito session is missing a username');
  }

  syncStorage.setString(SyncStorageKey.USER_ID, username);
  return session;
};

export const signOut = async (isGlobalSignOut: boolean = false): Promise<void> => {
  const { user } = await getUserAndSession();

  if (isGlobalSignOut) {
    await new Promise<void>((resolve, reject) =>
      user.globalSignOut({ onSuccess: () => resolve(), onFailure: reject })
    );
  } else {
    await new Promise<void>(resolve => user.signOut(() => resolve()));
  }

  syncStorage.remove(SyncStorageKey.USER_ID);
};

export const clearLocalAuthentication = (): null => {
  // Omitting the callback makes Cognito clear client data without attempting token revocation.
  getCognitoUserPool().getCurrentUser()?.signOut();
  syncStorage.remove(SyncStorageKey.USER_ID);
  return null;
};

export const signUp = ({
  username,
  wantsPromotionalEmails,
  name,
  phoneNumber,
  country,
  dob,
}: ISignUp): Promise<ISignUpResult> => {
  // TODO: customAttributes must be predefined using nickname as a hack
  const normalizedUsername = normalizeString(username);
  const password = uuidv4();
  const emailConsent = wantsPromotionalEmails ? 'true' : 'false';
  const attributes = attributeObjectToAttributeList({
    nickname: emailConsent,
    name,
    locale: country,
    phone_number: phoneNumber,
    birthdate: dob,
  });

  const userPool = getCognitoUserPool();

  return withNetworkRetries(
    () =>
      new Promise((resolve, reject) =>
        userPool.signUp(normalizedUsername, password, attributes, [], (err, result) => {
          if (err || result == null) {
            return reject(err);
          }
          return resolve(result);
        })
      ),
    getCognitoRetryConfig('Auth.signUp')
  );
};

export const validateLogin = async ({
  username,
  code,
  sessionId,
}: IValidateLogin): Promise<CognitoUserSession | null> => {
  const cognitoUser = getCognitoUserForUsername(username);
  (cognitoUser as any).Session = sessionId;

  return withNetworkRetries(
    () =>
      new Promise<CognitoUserSession>((resolve, reject) =>
        cognitoUser.sendCustomChallengeAnswer(code, {
          onSuccess: session => {
            resolve(session);
          },
          onFailure: err => {
            reject(err);
          },
        })
      ),
    getCognitoRetryConfig('Auth.sendCustomChallengeAnswer')
  );
};

export const updateUserAttributes = async ({ name, phoneNumber, dob }: IUpdateUserAttributes) => {
  const { user } = await getUserAndSession();

  const attributesToUpdate = attributeObjectToAttributeList({
    name,
    phone_number: phoneNumber || undefined,
    birthdate: dob,
  });

  return new Promise<string>((resolve, reject) =>
    user.updateAttributes(attributesToUpdate, (err, result) => {
      if (err || !result) {
        return reject(err);
      }
      return resolve(result);
    })
  );
};

const getCognitoUserForUsername = (username: string): CognitoUser => {
  const normalizedUsername = normalizeString(username);
  return new CognitoUser({
    Username: normalizedUsername,
    Pool: getCognitoUserPool(),
    Storage: secureAuthStorage,
  });
};

// getSession (in addition to getCurrentUser) is often required before logout/update
const getUserAndSession = async () => {
  return new Promise<{ user: CognitoUser; session: CognitoUserSession }>((resolve, reject) => {
    const user = getCognitoUserPool().getCurrentUser();
    if (!user) {
      reject(new Error('No current user'));
      return;
    }
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        return reject(err || new Error('No session'));
      }
      resolve({ user, session });
    });
  });
};

export const getCognitoUserPool = () => {
  const { userPoolClientId, userPoolId, region } = getConfigValue({
    key: 'aws',
    defaultValue: {},
  });

  return new CognitoUserPool({
    UserPoolId: userPoolId,
    ClientId: userPoolClientId,
    endpoint: `https://cognito-idp.${region}.amazonaws.com/`,
    Storage: secureAuthStorage,
  });
};
